import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { subscriptionsService } from '../../api/apiClient';
import { Card } from '../../components/UI/Card';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from '../../components/UI/Button';
import { Badge } from '../../components/UI/Badge';
import {
  CheckCircle2,
  ShieldCheck,
  Zap,
  HelpCircle,
} from 'lucide-react';

export const Subscriptions = () => {
  const { isSubscribed, user, refreshUser } = useAuth();
  const { success, error: toastError } = useToast();
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    subscriptionsService.getConfig()
      .then(res => {
        if (res.success && res.data.clientId) {
          setPaypalConfig(res.data);
        }
      })
      .catch(console.error);
  }, []);

  const handlePaypalSuccess = async (data) => {
    try {
      setSyncing(true);
      const res = await subscriptionsService.success(data.subscriptionID);
      if (res.success) {
        await refreshUser();
        success(res.message || '¡Suscripción Profesional activada exitosamente!');
      }
    } catch (err) {
      toastError(err.message || 'Error al validar la suscripción con el servidor.');
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("¿Estás seguro de que deseas cancelar tu suscripción a Pro?")) return;
    
    setSyncing(true);
    try {
      const res = await subscriptionsService.cancel();
      if (res.success) {
        await refreshUser();
        success(res.message || '¡Suscripción cancelada exitosamente!');
      }
    } catch (err) {
      toastError(err.message || 'Error al cancelar la suscripción.');
    } finally {
      setSyncing(false);
    }
  };

  const features = [
    'Facturas y cotizaciones ilimitadas',
    'Catálogo y control de inventario con alertas de stock mínimo',
    'Kardex y registro histórico de movimientos de stock',
    'Exportación de PDFs corporativos de alta resolución',
    'Múltiples monedas internacionales',
    'Soporte técnico preferencial 24/7',
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <Badge variant={isSubscribed ? 'success' : 'primary'} size="lg" dot>
          {isSubscribed ? 'Estado: Suscripción Activa (PRO)' : 'Estado: Plan Gratuito (Limitado)'}
        </Badge>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
          Planes & Facturación
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto">
          Elige el plan ideal para escalar la gestión de tus presupuestos, cobros y catálogo de inventario
        </p>
      </div>

      {/* Pricing Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        
        {/* Free Plan */}
        <Card className={`p-8 flex flex-col justify-between ${!isSubscribed ? 'border-[#08707A] ring-2 ring-[#08707A]/20' : ''}`}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Plan Inicial
              </span>
              {!isSubscribed && <Badge variant="primary" size="sm">Plan Actual</Badge>}
            </div>

            <div className="space-y-1">
              <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                Gratis
              </h2>
              <p className="text-xs text-slate-500">
                Para profesionales independientes y pruebas
              </p>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-3">
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Hasta 5 facturas gratuitas</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Hasta 1 cliente en directorio</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Catálogo de productos e inventario</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="w-4 h-4 text-center leading-4 font-bold text-slate-300">✕</span>
                <span className="line-through">Facturas ilimitadas</span>
              </div>
            </div>
          </div>

          <div className="pt-6">
            <Button variant="outline" className="w-full" disabled>
              {!isSubscribed ? 'Plan en Uso' : 'Plan Básico'}
            </Button>
          </div>
        </Card>

        {/* Pro Plan */}
        <Card className="p-8 flex flex-col justify-between bg-gradient-to-br from-teal-900/90 via-[#08707A] to-slate-900 text-white border-teal-700 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4">
            <span className="px-3 py-1 bg-amber-400 text-slate-950 font-black text-[10px] rounded-full uppercase tracking-wider shadow">
              Recomendado
            </span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-teal-200">
                Plan Profesional
              </span>
            </div>

            <div className="space-y-1">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black font-sans">$5.99</span>
                <span className="text-xs text-teal-200 font-semibold">/ mes</span>
              </div>
              <p className="text-xs text-teal-100">
                Facturación sin límites para empresas y negocios en crecimiento
              </p>
            </div>

            <div className="pt-4 border-t border-white/10 space-y-3">
              {features.map((feat, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-teal-50">
                  <CheckCircle2 className="w-4 h-4 text-amber-300 flex-shrink-0" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 space-y-4">
            {isSubscribed ? (
              user?.paypal_subscription_id ? (
                <>
                  <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-emerald-500/30 border border-emerald-400/40 text-emerald-200 text-xs font-bold">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                    Tu Plan Pro está Activo
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={syncing}
                    onClick={handleCancelSubscription}
                    className="w-full text-white border-white/20 hover:bg-white/10"
                  >
                    Cancelar Suscripción
                  </Button>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/20 border border-amber-400/30 text-amber-100 text-xs font-medium leading-relaxed">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-300" />
                    <span>
                      <strong>Suscripción Cancelada.</strong> Disfrutarás de tus beneficios Pro hasta el <strong>{user?.subscription_ends_at ? new Date(user.subscription_ends_at).toLocaleDateString() : 'final del ciclo'}</strong>.
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled
                    className="w-full text-white/50 border-white/10 bg-white/5 cursor-not-allowed"
                  >
                    Podrás renovar al finalizar tu periodo
                  </Button>
                </div>
              )
            ) : (
              paypalConfig && paypalConfig.clientId ? (
                <PayPalScriptProvider options={{ "client-id": paypalConfig.clientId, vault: true, intent: "subscription" }}>
                  <PayPalButtons
                    style={{ shape: 'rect', color: 'gold', layout: 'vertical', label: 'subscribe' }}
                    createSubscription={(data, actions) => {
                      return actions.subscription.create({
                        plan_id: paypalConfig.planId
                      });
                    }}
                    onApprove={(data, actions) => handlePaypalSuccess(data)}
                    onError={(err) => {
                      console.error('PayPal Error:', err);
                      toastError('Ocurrió un error con PayPal. Por favor verifica tu método de pago.');
                    }}
                  />
                </PayPalScriptProvider>
              ) : (
                <Button
                  variant="secondary"
                  size="lg"
                  icon={Zap}
                  disabled
                  className="w-full bg-white text-[#08707A] hover:bg-teal-50 font-extrabold shadow-lg opacity-50"
                >
                  Cargando Pago...
                </Button>
              )
            )}
          </div>
        </Card>
      </div>

      {/* FAQ or Security footer */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-teal-50 dark:bg-teal-950/60 rounded-2xl text-[#08707A] dark:text-teal-400 flex-shrink-0">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Garantía de Satisfacción y Seguridad
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
              Todos los cobros se gestionan de forma segura con cifrado bancario de 256 bits. Puedes cancelar o actualizar tu suscripción en cualquier momento sin compromisos a largo plazo.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
